import { useState, useEffect, useCallback } from 'react'
import type { Account, AccountType, Member } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { accountsApi, membersApi } from '../lib/api'
import { AccountModal, AccountTile } from '../components/AccountShared'
import { CsvImportModal } from '../components/CsvImportModal'
import { SortableCardGrid } from '../components/SortableCardGrid'
import { formatsForAccount } from '../lib/csv-import'
import { accountOrderItems, moveAccount } from '../lib/account-order'
import type { TranslationKey } from '../i18n/translations'
import { btnPrimary, btnSecondary, pageSubtitle, pageTitle, sectionTitle } from '../lib/ui'

// 証券口座（investment）はここでは扱わない。保有商品と同じ画面で登録できるよう
// 証券口座ページ（/investments）に寄せてあり、この画面は銀行口座と現金・その他だけを見る
const PAGE_TYPES: AccountType[] = ['checking', 'savings', 'cash', 'other']

type Section = 'bank' | 'cash' | 'other'

const SECTION_ORDER: Section[] = ['bank', 'cash', 'other']

const SECTION_TITLE_KEYS: Record<Section, TranslationKey> = {
  bank: 'accounts_section_bank',
  cash: 'accounts_section_cash',
  other: 'accounts_section_other',
}

/** 所有者が未設定の口座をまとめるグループのキー。メンバーIDと衝突しない値にする */
const NO_MEMBER_KEY = '__no_member__'

function sectionOf(type: AccountType): Section {
  switch (type) {
    case 'checking':
    case 'savings':
      return 'bank'
    case 'cash':
      return 'cash'
    default:
      return 'other'
  }
}

export default function Accounts() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [csvAccount, setCsvAccount] = useState<Account | null>(null)
  const [sorting, setSorting] = useState(false)

  const fetchAccounts = useCallback(() => {
    if (!accessToken) return
    setLoading(true)
    accountsApi.list(accessToken, { includeArchived: true })
      .then((res) => setAccounts(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setLoading(false))
  }, [accessToken, t])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  // 口座を所有者ごとに並べるために使う。取得に失敗しても口座一覧は表示する
  useEffect(() => {
    if (!accessToken) return
    void membersApi.list(accessToken).then((res) => setMembers(res.data)).catch(() => undefined)
  }, [accessToken])

  // アーカイブの切り替えは編集モーダルの中から呼ばれる。失敗の理由はモーダル内に
  // 出したいので、ここでは受け取らず throw させる
  const handleArchive = async (account: Account) => {
    if (!accessToken) return
    await accountsApi.update(accessToken, account.id, { isArchived: !account.isArchived })
    fetchAccounts()
  }

  // 完全削除。取引や連携が残っている口座はサーバーが 409 で拒否するので、
  // その理由は呼び出し元（編集モーダル）に投げてモーダル内に表示させる
  const handleDelete = async (account: Account) => {
    if (!accessToken) return
    await accountsApi.delete(accessToken, account.id, { permanent: true })
    fetchAccounts()
  }

  // 並べ替え。表示順は全口座で 1 本の並びなので、動かした結果を全体に反映してから
  // 通し番号で保存する。画面は先に更新し、保存に失敗したら取り直して元に戻す
  const handleMove = async (movingId: string, targetId: string) => {
    if (!accessToken) return
    const next = moveAccount(accounts, movingId, targetId)
    if (next === accounts) return
    setAccounts(next)
    try {
      await accountsApi.reorder(accessToken, { items: accountOrderItems(next) })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('accounts_reorder_error'))
      fetchAccounts()
    }
  }

  // クレジットカードと証券口座はそれぞれ専用ページで扱う
  const pageAccounts = accounts.filter((a) => a.type !== 'credit_card' && a.type !== 'investment')
  const filteredAccounts = showArchived
    ? pageAccounts
    : pageAccounts.filter((a) => !a.isArchived)

  const sectionsOf = (list: Account[]) => SECTION_ORDER
    .map((section) => ({
      section,
      accounts: list.filter((a) => sectionOf(a.type) === section),
    }))
    .filter((s) => s.accounts.length > 0)

  const sumBalance = (list: Account[]) => list.reduce((sum, a) => sum + a.balance, 0)

  const memberIds = new Set(members.map((m) => m.id))
  // メンバーの表示順どおりに並べ、所有者未設定（と、既に消えたメンバーを指す口座）は最後
  const groups = [
    ...members.map((m) => ({ key: m.id, name: m.name, accounts: filteredAccounts.filter((a) => a.memberId === m.id) })),
    {
      key: NO_MEMBER_KEY,
      name: t('accounts_member_none_group'),
      accounts: filteredAccounts.filter((a) => !a.memberId || !memberIds.has(a.memberId)),
    },
  ].filter((g) => g.accounts.length > 0)

  const formattedTotal = formatJPY(sumBalance(filteredAccounts))

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={pageTitle}>{t('accounts_title')}</h1>
          <p className={pageSubtitle}>
            {t('accounts_total_balance')}:{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formattedTotal}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer dark:text-gray-300">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded"
            />
            {t('accounts_show_archived')}
          </label>
          <button
            onClick={() => setSorting((v) => !v)}
            className={sorting ? btnPrimary : btnSecondary}
          >
            {sorting ? t('accounts_reorder_done') : t('accounts_reorder')}
          </button>
          <button
            onClick={() => { setEditingAccount(null); setModalOpen(true) }}
            className={btnPrimary}
          >
            {t('accounts_add')}
          </button>
        </div>
      </div>

      {sorting && (
        <p className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300">
          {t('accounts_reorder_hint')}
        </p>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-4 flex justify-center py-12">
          <p className="text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="mt-4 py-12 text-center text-gray-400">
          <p className="text-sm">{t('accounts_no_result')}</p>
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.key} className="mt-8">
            <h2 className={sectionTitle}>
              {group.name}
              <span className="ml-2 text-sm font-normal text-gray-400 dark:text-gray-500">
                {t('accounts_total_balance')}: {formatJPY(sumBalance(group.accounts))}
              </span>
            </h2>
            {sectionsOf(group.accounts).map(({ section, accounts: sectionAccounts }) => (
              <div key={section} className="mt-4">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  {t(SECTION_TITLE_KEYS[section])}
                </h3>
                <SortableCardGrid items={sectionAccounts} sorting={sorting} onMove={handleMove}>
                  {(account) => (
                    <AccountTile
                      account={account}
                      onEdit={(a) => { setEditingAccount(a); setModalOpen(true) }}
                      onCsvImport={formatsForAccount(account).length > 0 ? (a) => setCsvAccount(a) : undefined}
                      transactionsHref={`/transactions?accountId=${account.id}`}
                    />
                  )}
                </SortableCardGrid>
              </div>
            ))}
          </div>
        ))
      )}

      <AccountModal
        open={modalOpen}
        initial={editingAccount}
        allowedTypes={PAGE_TYPES}
        onClose={() => setModalOpen(false)}
        onSaved={fetchAccounts}
        onDelete={handleDelete}
        onArchive={handleArchive}
        token={accessToken ?? ''}
      />

      <CsvImportModal
        open={csvAccount !== null}
        account={csvAccount}
        token={accessToken ?? ''}
        onClose={() => setCsvAccount(null)}
        onImported={fetchAccounts}
      />
    </div>
  )
}
